/**
 * THE breakpoints. Defined once, here.
 *
 * CSS custom properties can't be used inside `@media (min-width: …)` queries,
 * so these numbers necessarily exist twice: once in TypeScript (for
 * `useSurface()` / `matchMedia`) and once, literally, in
 * `src/styles/wordrush-desktop.css`. The CSS file documents them at the top
 * and MUST be kept in sync with this file. There is no third copy.
 *
 *   base   0–767    the phone. The protected artifact. Never modified.
 *   md     768–1023 one comfortable column. BottomNav stays.
 *   lg     1024+    the desktop shell. Nav rail, 3-zone stages, hotkeys.
 *   xl     1440+    scale-up caps only. Nothing structural.
 */
export const BP = {
  md: 768,
  lg: 1024,
  xl: 1440,
} as const;

/**
 * The only three surfaces the app knows about.
 *
 * Deliberately NOT a lean-back/TV surface: a laptop propped up for the
 * living room is a thing the *user* declares by clicking through to
 * `/tv/:code`. We never infer it. A wrong lean-back guess in a family
 * product is a trust breach.
 */
export type Surface = 'phone' | 'tablet' | 'desktop';

/** Media query strings, for `matchMedia`. */
export const MQ = {
  md: `(min-width: ${BP.md}px)`,
  lg: `(min-width: ${BP.lg}px)`,
  xl: `(min-width: ${BP.xl}px)`,
  /** A real pointer — mouse or trackpad. Gates hover + keycap hints. */
  fine: '(hover: hover) and (pointer: fine)',
} as const;
